import { Alert, Box, Tab, Tabs, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { hasDemand } from "lib/authorization/has-demand"
import { useRef } from "react"
import { SSOConfigPanel } from "routes/settings/plugins/sso/components/sso-config-panel"
import { IdpSettingsPanel } from "routes/settings/plugins/sso/components/idp-settings-panel"
import { ScimConnectionSection } from "routes/settings/plugins/scim/components/scim-connection-section"
import { GroupMappingsSection } from "routes/settings/plugins/scim/components/group-mappings-section"
import { ActivityLogSection } from "routes/settings/plugins/scim/components/activity-log-section"
import { identityProviderAvailable } from "routes/settings/plugins/scim/controller/identity-provider-available"
import { getSSOConfig } from "routes/settings/plugins/sso/controller/get-sso-config"
import { Switch, Case } from "lib/@components/switch"

export function IdpPanel() {
    const [subtab, setSubtab] = useSearchParam("idp", "settings")
    const { client } = useBoundContext()
    const isAdmin = hasDemand("clientAdmin") || hasDemand("admin")
    const clientId = client?.id

    const tierResult = identityProviderAvailable.useResults({ clientId })
    const hasTier = tierResult === true
    const tierLoading = tierResult === undefined

    const emptyConfig = useRef({})
    const config = getSSOConfig.useResults({ clientId }) ?? emptyConfig.current
    const ssoEnabled = !!config.enabled
    const scimEnabled = !!config.scimEnabled

    const tierState = tierLoading ? "loading" : hasTier ? "active" : "paywall"

    // If the active tab belongs to a section that is now off, fall back to settings
    const ssoTabs = ["sso"]
    const scimTabs = ["scim", "mappings"]
    const effectiveTab = getEffectiveTab(subtab, ssoEnabled, scimEnabled)

    if (!isAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    Identity provider settings are only available to client admins.
                </Typography>
            </Box>
        )
    }

    return (
        <Box>
            {tierState === "active" && (
                <Tabs
                    value={effectiveTab}
                    onChange={(_, next) => setSubtab(next)}
                    sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}
                >
                    <Tab value="settings" label="Settings" />
                    {ssoEnabled && <Tab value="sso" label="SSO" />}
                    {scimEnabled && <Tab value="scim" label="SCIM connection" />}
                    {scimEnabled && <Tab value="mappings" label="Group mappings" />}
                    <Tab value="activity" label="Activity" />
                </Tabs>
            )}

            <Switch value={tierState}>
                <Case when="loading">
                    <Box sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Checking plan…
                        </Typography>
                    </Box>
                </Case>
                <Case when="paywall">
                    <Box sx={{ p: 3 }}>
                        <Alert severity="info" data-cy="idp-tier-paywall">
                            <Typography variant="subtitle2" gutterBottom>
                                Identity provider integration is not enabled for your account.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Facilities-iQ can integrate with your organisation's IDP for Single Sign-On and
                                Automatic User Provisioning. Speak to your account manager to find out more.
                            </Typography>
                        </Alert>
                    </Box>
                </Case>
                <Case when="active">
                    <>
                        {effectiveTab === "settings" && <IdpSettingsPanel onChanged={handleSettingsChanged} />}
                        {effectiveTab === "sso" && <SSOConfigPanel />}
                        {effectiveTab === "scim" && <ScimConnectionSection client={client} />}
                        {effectiveTab === "mappings" && (
                            <Box sx={{ p: 3 }}>
                                <GroupMappingsSection />
                            </Box>
                        )}
                        {effectiveTab === "activity" && (
                            <Box sx={{ p: 3 }}>
                                <ActivityLogSection />
                            </Box>
                        )}
                    </>
                </Case>
            </Switch>
        </Box>
    )

    function handleSettingsChanged() {
        // getSSOConfig uses useResults so it re-fetches automatically on invalidate
    }

    function getEffectiveTab(tab, ssoOn, scimOn) {
        if (ssoTabs.includes(tab) && !ssoOn) return "settings"
        if (scimTabs.includes(tab) && !scimOn) return "settings"
        return tab
    }
}
